"use client";

import { useState } from "react";
import { devLabelPanel } from "@/lib/dev-labels";

export interface HelperFallbackBannerProps {
	visible: boolean;
	onDismiss?: () => void;
}

/**
 * HelperFallbackBanner — Phase 25-AB Task 18 #15 (panel 76 / 9-I).
 *
 * Banner that appears between the Welcome header and the body when Hybrid
 * Helper is missing or fell back to cloud. Visibility is driven by the parent
 * (`<HelperStatusDot>` cycle / actual Helper ping in production).
 *
 * Mockup source: docs/mockups/welcome.html lines 323-329.
 */
export function HelperFallbackBanner({
	visible,
	onDismiss,
}: HelperFallbackBannerProps) {
	const [dismissed, setDismissed] = useState(false);
	if (!visible || dismissed) return null;
	return (
		<div
			role="status"
			aria-live="polite"
			data-testid="helper-fallback-banner"
			style={{
				display: "flex",
				margin: "6px 0 0",
				padding: "9px 13px",
				borderRadius: 8,
				background: "rgba(239,68,68,0.08)",
				border: "1px solid rgba(239,68,68,0.3)",
				alignItems: "center",
				gap: 8,
				fontSize: 11,
			}}
			{...devLabelPanel("9-I")}
		>
			<svg
				width="16"
				height="16"
				viewBox="0 0 24 24"
				style={{ color: "#ef4444", flexShrink: 0 }}
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			>
				<path d="M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
				<line x1="12" y1="9" x2="12" y2="13" />
				<line x1="12" y1="17" x2="12.01" y2="17" />
			</svg>
			<span style={{ flex: 1, color: "#fcd2d2" }}>
				<strong style={{ color: "#ef4444" }}>Hybrid Helper 미연결</strong> · 영상
				분석/렌더링이 클라우드를 통해 처리됩니다. Helper 데스크톱 앱 설치 시 더 빠른
				처리 + 무제한 분량.
			</span>
			<button
				type="button"
				style={{
					padding: "5px 11px",
					borderRadius: 5,
					background: "#d4af37",
					color: "#0a0a0a",
					border: "none",
					fontSize: 10,
					fontWeight: 700,
					cursor: "pointer",
				}}
				onClick={() => {
					if (typeof window !== "undefined") {
						window.location.assign("/dev/helper-install");
					}
				}}
			>
				Helper 다운로드 ↗
			</button>
			<button
				type="button"
				onClick={() => {
					setDismissed(true);
					onDismiss?.();
				}}
				style={{
					padding: "5px 11px",
					borderRadius: 5,
					background: "transparent",
					border: "1px solid rgba(255,255,255,0.1)",
					color: "#a3a3a3",
					fontSize: 10,
					cursor: "pointer",
				}}
			>
				잠시 숨기기
			</button>
		</div>
	);
}
